import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';

export interface BulkDeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  count: number;
  resourceLabel: string;
  isDeleting: boolean;
  deletedCount?: number;
  byFilters?: boolean;
}

const BulkDeleteConfirmDialog: React.FC<BulkDeleteConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  count,
  resourceLabel,
  isDeleting,
  deletedCount,
  byFilters = false,
}) => {
  const translate = useTranslate();
  const isComplete = deletedCount !== undefined;

  return (
    <Dialog open={open} onClose={isDeleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: isComplete ? 'success.main' : 'error.main' }}>
        {translate(`resources.${resourceLabel}.bulkDelete.confirmTitle`)}
      </DialogTitle>
      <DialogContent>
        {isComplete ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {translate(`resources.${resourceLabel}.bulkDelete.successMessage`, {
                count: deletedCount,
              })}
            </Alert>
          </Box>
        ) : (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {byFilters
                ? translate(`resources.${resourceLabel}.bulkDelete.confirmByFiltersMessage`)
                : translate(`resources.${resourceLabel}.bulkDelete.confirmMessage`, {
                    count,
                  })}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {translate(`resources.${resourceLabel}.bulkDelete.irreversible`)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {isComplete ? (
          <Button onClick={onClose} variant="contained">
            {translate('ra.action.close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={isDeleting} variant="outlined">
              {translate('ra.action.cancel')}
            </Button>
            <Button
              onClick={onConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={
                isDeleting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {isDeleting
                ? translate(`resources.${resourceLabel}.bulkDelete.deleting`)
                : translate(`resources.${resourceLabel}.bulkDelete.deleteSelected`)}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkDeleteConfirmDialog;
